import moment from 'moment';

import { DateFormat } from '../../../../utils/date';
import { WorkoutFeedViewType } from '../utils';

const KM_PER_MILE = 1.609344;
const DEFAULT_MAX_HEART_RATE = 190;
// Heart rate is stored as one point per minute.
const HEART_RATE_POINT_SECONDS = 60;
// For utils/time's formatDuration: "3h 22m" or "52m".
const ACTIVE_TIME_FORMAT = 'h[h] m[m]';

// Lower bound of each zone as a share of max heart rate.
const HeartRateZones = [
  {
    id: 'Z1', label: 'Warm-up', minRatio: 0, description: 'Very light, recovery',
  },
  {
    id: 'Z2', label: 'Easy', minRatio: 0.6, description: 'Comfortable, builds endurance',
  },
  {
    id: 'Z3', label: 'Aerobic', minRatio: 0.7, description: 'Moderate, steady effort',
  },
  {
    id: 'Z4', label: 'Threshold', minRatio: 0.8, description: 'Hard, sustainable for minutes',
  },
  {
    id: 'Z5', label: 'Max', minRatio: 0.9, description: 'All-out, short bursts',
  },
];

// Workout types whose rows can carry synced health data.
const HEALTH_LINKABLE_TYPES = [
  WorkoutFeedViewType.COACH_ASSIGNED_WORKOUT,
  WorkoutFeedViewType.SELF_ASSIGNED_WORKOUT,
  WorkoutFeedViewType.TRACKED_ACTIVITY,
];

const toMillis = (date) => {
  if (!date) return null;
  if (typeof date.toMillis === 'function') return date.toMillis();
  if (date instanceof Date) return date.getTime();
  return typeof date === 'number' ? date : null;
};

// Check-in workout rows have no id, so type, start and title identify them.
const getWorkoutKey = ({ type, startDate, title }) => `${type}|${toMillis(startDate)}|${(title ?? '').trim()}`;

// One key per row, in order; identical rows (two "Running" the same day) get #0, #1…
const getWorkoutKeys = (rows) => {
  const seen = new Map();
  return new Map(rows.map((row) => {
    const key = getWorkoutKey(row);
    const occurrence = seen.get(key) ?? 0;
    seen.set(key, occurrence + 1);
    return [row, `${key}#${occurrence}`];
  }));
};

const isHealthLinkable = (workout) => (
  HEALTH_LINKABLE_TYPES.includes(workout.type)
  && (workout.type === WorkoutFeedViewType.TRACKED_ACTIVITY || !!workout.isCompleted)
);

const getMaxHeartRate = (age) => (age > 0 ? 220 - age : DEFAULT_MAX_HEART_RATE);

// Each zone's bpm range; the top zone has no upper bound.
const getZoneRanges = (maxHeartRate) => HeartRateZones.map((zone, index) => {
  const next = HeartRateZones[index + 1];
  return {
    ...zone,
    minBpm: Math.round(maxHeartRate * zone.minRatio),
    maxBpm: next ? Math.round(maxHeartRate * next.minRatio) - 1 : null,
  };
});

// The highest zone whose lower bound the bpm reaches, the same boundaries the mobile app uses.
const getZoneIndex = (bpm, maxHeartRate) => {
  let zoneIndex = 0;
  getZoneRanges(maxHeartRate).forEach(({ minBpm }, index) => {
    if (bpm >= minBpm) zoneIndex = index;
  });
  return zoneIndex;
};

const pointHeartRate = ({ minHeartRate, maxHeartRate }) => (minHeartRate + maxHeartRate) / 2;

// Seconds per zone, Z1 first: each per-minute point counts one minute, as on mobile.
const getTimeInZones = (points, maxHeartRate) => {
  const seconds = HeartRateZones.map(() => 0);
  points.forEach((point) => {
    seconds[getZoneIndex(pointHeartRate(point), maxHeartRate)] += HEART_RATE_POINT_SECONDS;
  });
  return seconds;
};

const milesToKm = (miles) => miles * KM_PER_MILE;

const formatNumber = (value) => Math.round(value).toLocaleString();

// The week's totals across workouts with health data.
const summarizeWeek = ({
  workoutList,
  healthByWorkoutKey,
  workoutKeys,
  dayNames,
  maxHeartRate,
}) => {
  const linkableWorkouts = workoutList.filter(isHealthLinkable);
  const caloriesByDay = Object.fromEntries(dayNames.map((day) => [
    day,
    { calories: 0, hasData: false, hasWorkouts: false },
  ]));
  workoutList.forEach((workout) => {
    const day = moment(workout.startDate.toDate()).format(DateFormat.WEEK_DAY_NAME);
    if (caloriesByDay[day]) caloriesByDay[day].hasWorkouts = true;
  });

  const totals = {
    syncedCount: 0,
    linkableCount: linkableWorkouts.length,
    duration: 0,
    activeCalories: 0,
    basalCalories: 0,
    distanceKm: 0,
    heartRateSum: 0,
    heartRateWeight: 0,
    peakHeartRate: 0,
    peakDay: '',
    zoneSeconds: HeartRateZones.map(() => 0),
    sourceNames: [],
  };

  linkableWorkouts.forEach((workout) => {
    const activity = healthByWorkoutKey.get(workoutKeys.get(workout));
    if (!activity) return;
    totals.syncedCount += 1;
    totals.duration += activity.duration;
    totals.activeCalories += activity.activeCalories;
    totals.basalCalories += activity.basalEnergyBurned;
    totals.distanceKm += milesToKm(activity.distance);
    if (activity.averageHeartRate) {
      // Weighted by duration so a long session counts more than a short one.
      const weight = activity.duration || 1;
      totals.heartRateSum += activity.averageHeartRate * weight;
      totals.heartRateWeight += weight;
    }
    if (activity.maxHeartRate > totals.peakHeartRate) {
      totals.peakHeartRate = activity.maxHeartRate;
      totals.peakDay = moment(workout.startDate.toDate()).format('ddd');
    }
    getTimeInZones(activity.heartRateDataPoints, maxHeartRate).forEach((seconds, index) => {
      totals.zoneSeconds[index] += seconds;
    });
    if (activity.sourceName && !totals.sourceNames.includes(activity.sourceName)) {
      totals.sourceNames.push(activity.sourceName);
    }
    const day = caloriesByDay[moment(workout.startDate.toDate()).format(DateFormat.WEEK_DAY_NAME)];
    if (day) {
      day.calories += activity.activeCalories;
      day.hasData = true;
    }
  });

  return {
    ...totals,
    averageHeartRate: totals.heartRateWeight ? Math.round(totals.heartRateSum / totals.heartRateWeight) : null,
    totalCalories: totals.activeCalories + totals.basalCalories,
    caloriesByDay: dayNames.map((day) => ({ day, ...caloriesByDay[day] })),
  };
};

// Chart coordinates in a width x height box, leaving `left` and `bottom` for the axis labels.
const buildHeartRateChart = ({
  points,
  startDate,
  endDate,
  averageHeartRate,
  maxHeartRate,
  width = 680,
  height = 170,
  left = 30,
  bottom = 20,
}) => {
  const plotHeight = height - bottom;
  const plotWidth = width - left;
  const values = points.map(pointHeartRate);
  const low = Math.floor((Math.min(...points.map((p) => p.minHeartRate)) - 5) / 10) * 10;
  const high = Math.ceil((Math.max(...points.map((p) => p.maxHeartRate)) + 5) / 10) * 10;
  const start = toMillis(startDate) ?? points[0].date.getTime();
  const end = Math.max(toMillis(endDate) ?? 0, points[points.length - 1].date.getTime());
  const span = Math.max(end - start, 1);

  const xOf = (date) => left + ((date.getTime() - start) / span) * plotWidth;
  const yOf = (bpm) => plotHeight - ((bpm - low) / (high - low)) * plotHeight;
  const clampY = (bpm) => Math.min(plotHeight, Math.max(0, yOf(bpm)));

  const line = points.map((point, index) => ({ x: xOf(point.date), y: yOf(values[index]) }));
  const peakIndex = values.indexOf(Math.max(...values));

  // Zone bands, top (Z5) down, clipped to the visible range.
  const bands = HeartRateZones.map((zone, index) => {
    const nextRatio = HeartRateZones[index + 1]?.minRatio;
    const top = nextRatio ? clampY(nextRatio * maxHeartRate) : 0;
    const bottomY = clampY(zone.minRatio * maxHeartRate);
    return { zoneIndex: index, y: top, height: Math.max(0, bottomY - top) };
  }).filter((band) => band.height > 0);

  const yTicks = [0.2, 0.5, 0.8].map((share) => {
    const bpm = Math.round(high - share * (high - low));
    return { bpm, y: yOf(bpm) };
  });

  const totalMinutes = Math.round(span / 60000);
  const xTicks = [0, 0.25, 0.5, 0.75, 1].map((share) => ({
    x: left + share * plotWidth,
    minute: Math.round(share * totalMinutes),
  }));

  return {
    points: line.map(({ x, y }) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' '),
    peak: { ...line[peakIndex], bpm: Math.round(values[peakIndex]) },
    averageY: averageHeartRate ? yOf(averageHeartRate) : null,
    bands,
    yTicks,
    xTicks,
    totalMinutes,
    plotHeight,
    left,
    width,
    height,
  };
};

export {
  HeartRateZones,
  getWorkoutKey,
  getWorkoutKeys,
  isHealthLinkable,
  getMaxHeartRate,
  getZoneRanges,
  getTimeInZones,
  milesToKm,
  ACTIVE_TIME_FORMAT,
  formatNumber,
  summarizeWeek,
  buildHeartRateChart,
};
